import { forwardRef, useEffect, useRef } from "react";
import { PassThroughPropsWithRef } from "../../types";
import cx from "classnames";
import styles from "./DrillDown.module.scss";
import { FocusTrap } from "focus-trap-react";

import { useDrillDownContext } from "./internal/useDrillDownContext";

import { DrillDownHeader } from "./DrillDownHeader";
import { DrillDownContent } from "./DrillDownContent";
import { DrillDownFooter } from "./DrillDownFooter";
import { DrillDownNextButton } from "./DrillDownNextButton";
import { DrillDownPrevButton } from "./DrillDownPrevButton";

/**
 * Props for the DrillDown component.
 *
 * @property index - Zero-based index of this drilldown screen in the navigation sequence.
 * @property onClose - Called when the close button in the header is clicked. When provided, displays a close button in the DrillDownHeader.
 *
 * @extends PassThroughPropsWithRef<"div">
 */
export type DrillDownProps = PassThroughPropsWithRef<"div"> & {
  index: number;

  onClose?: () => void;
};

/**
 * Multi-step navigation component for Dialog, Drawer, and Page.Panel.
 *
 * Manages sequential navigation between related content screens within overlay components.
 * Each DrillDown represents a single screen identified by its index. Navigate between screens
 * using DrillDown.NextButton, DrillDown.PrevButton, or the useDrillDown hook.
 *
 * Features:
 * - Index-based navigation between screens
 * - Built-in back and close button support in headers
 * - Automatic analytics tracking via data-tracking-id
 * - Controlled and uncontrolled modes via useDrillDown hook
 * - Compound component API for consistent structure
 *
 * @example
 * ```tsx
 * <Dialog defaultDrillDownIndex={0}>
 *   <DrillDown index={0}>
 *     <DrillDown.Header>Step 1</DrillDown.Header>
 *     <DrillDown.Content>First step content</DrillDown.Content>
 *     <DrillDown.Footer>
 *       <DrillDown.NextButton>Continue</DrillDown.NextButton>
 *     </DrillDown.Footer>
 *   </DrillDown>
 *   <DrillDown index={1} onClose={() => close()}>
 *     <DrillDown.Header>Step 2</DrillDown.Header>
 *     <DrillDown.Content>Second step content</DrillDown.Content>
 *     <DrillDown.Footer>
 *       <DrillDown.PrevButton>Back</DrillDown.PrevButton>
 *     </DrillDown.Footer>
 *   </DrillDown>
 * </Dialog>
 * ```
 */
export const DrillDown = Object.assign(
  forwardRef<HTMLDivElement, DrillDownProps>(
    function DrillDownInner(props, ref) {
      const { index: indexProp, onClose, className, children, ...rest } = props;
      const {
        index,
        setRegisteredIndices,
        setOnClose,
        hostIsInteractive = true,
      } = useDrillDownContext();
      const sectionRef = useRef<HTMLElement>(null);

      const isActiveScreen = hostIsInteractive && index === indexProp;

      // register our index with the DrillDown context when we're rendered
      // so we know when we're past the last index
      useEffect(() => {
        setRegisteredIndices?.((registeredIndices) => {
          if (registeredIndices == null) return registeredIndices;

          const nextRegisteredIndices = [...registeredIndices];

          if (nextRegisteredIndices[indexProp] == null) {
            nextRegisteredIndices[indexProp] = 1;
            return nextRegisteredIndices;
          }

          nextRegisteredIndices[indexProp] += 1;
          return nextRegisteredIndices;
        });

        return () => {
          setRegisteredIndices?.((registeredIndices) => {
            if (registeredIndices == null) return registeredIndices;

            const nextRegisteredIndices = [...registeredIndices];

            if (nextRegisteredIndices[indexProp] === 1) {
              delete nextRegisteredIndices[indexProp];
              return nextRegisteredIndices;
            }

            if (nextRegisteredIndices[indexProp] > 1) {
              nextRegisteredIndices[indexProp] -= 1;
              return nextRegisteredIndices;
            }

            return nextRegisteredIndices;
          });
        };
      }, [indexProp, setRegisteredIndices]);

      useEffect(() => {
        if (onClose && indexProp != null) {
          setOnClose?.(onClose, indexProp);
        }
      }, [setOnClose, onClose, indexProp]);

      return isActiveScreen ? (
        <div {...rest} className={cx(styles["drilldown"], className)} ref={ref}>
          <FocusTrap
            active={hostIsInteractive}
            focusTrapOptions={{
              returnFocusOnDeactivate: false,
              fallbackFocus: () => sectionRef.current ?? document.body,
            }}
          >
            <section
              className={styles["container"]}
              ref={sectionRef}
              tabIndex={-1}
            >
              {children}
            </section>
          </FocusTrap>
        </div>
      ) : null;
    },
  ),
  {
    Header: DrillDownHeader,
    Content: DrillDownContent,
    Footer: DrillDownFooter,
    NextButton: DrillDownNextButton,
    PrevButton: DrillDownPrevButton,
  },
);
DrillDown.displayName = "DrillDown";
export const DrillDownElement = DrillDown;
